import React from "react";
import { useT } from "@/services/i18n";
import { privacyContent } from "@/data/privacyContent";

// Rend une chaîne avec **gras** et *italique* (sous-ensemble markdown simple).
function renderInline(text) {
  const parts = String(text).split(/(\*\*[^*]+\*\*|\*[^*]+\*)/g);
  return parts.map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) return <strong key={i}>{p.slice(2, -2)}</strong>;
    if (p.startsWith("*") && p.endsWith("*")) return <em key={i}>{p.slice(1, -1)}</em>;
    return <React.Fragment key={i}>{p}</React.Fragment>;
  });
}

function Block({ b }) {
  if (b.t === "p") return <p>{renderInline(b.x)}</p>;
  if (b.t === "h3") return <h3 className="font-semibold">{renderInline(b.x)}</h3>;
  if (b.t === "note")
    return (
      <blockquote className="rounded-2xl border-l-4 border-[#6E66D6] bg-white/60 px-4 py-3 text-sm">
        {renderInline(b.x)}
      </blockquote>
    );
  if (b.t === "ul")
    return (
      <ul className="ml-5 list-disc space-y-1">
        {b.x.map((it, i) => (
          <li key={i}>{renderInline(it)}</li>
        ))}
      </ul>
    );
  if (b.t === "table")
    return (
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr className="border-b border-[#D9D7E6] text-left">
              {b.head.map((h, i) => (
                <th key={i} className="py-2 pr-3 font-semibold">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {b.rows.map((row, i) => (
              <tr key={i} className="border-b border-[#E5E3F0] align-top">
                {row.map((cell, j) => (
                  <td key={j} className="py-2 pr-3">{renderInline(cell)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  return null;
}

// Page publique de politique de confidentialité (accessible sans connexion).
// URL : /confidentialite. Suit la langue choisie (FR/EN/RU). Mise en page sobre.
export default function PrivacyPolicyScreen() {
  const { lang } = useT();
  const c = privacyContent[lang] || privacyContent.fr;

  return (
    <div className="min-h-[100dvh] bg-[#F4F3F8] text-[#2A2740]" data-testid="privacy-policy-page">
      <div className="mx-auto w-full max-w-2xl px-5 py-10 sm:px-8 sm:py-14">
        <a
          href="/"
          data-testid="privacy-back-link"
          className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-[#6E66D6] transition-opacity hover:opacity-70"
        >
          <span aria-hidden>&larr;</span> {c.back}
        </a>

        <article className="space-y-6 leading-relaxed">
          <header className="space-y-2">
            <h1 className="brand text-3xl font-bold sm:text-4xl">{c.title}</h1>
            <p className="text-sm text-[#6B6880]">
              <strong>{c.updated}</strong>
            </p>
          </header>

          {c.intro.map((p, i) => (
            <p key={i}>{renderInline(p)}</p>
          ))}

          {c.sections.map((s, i) => (
            <section key={i} className="space-y-3 pt-4">
              <h2 className="text-xl font-semibold sm:text-2xl">{s.title}</h2>
              {s.blocks.map((b, j) => (
                <Block key={j} b={b} />
              ))}
            </section>
          ))}
        </article>
      </div>
    </div>
  );
}
